import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Upload, Loader2, CheckCircle2, Star, Image as ImageIcon } from "lucide-react";

const RECOMMENDED = "Recommended creative size: 1200 x 450 px (8:3 ratio).";
const emptyForm = { id: null, name: "", creative_url: "", destination_url: "" };

export default function AdminPremierPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/premier-partners"); setPartners(data.partners || []); }
    catch { setPartners([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const openAdd = () => { setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => { setForm({ id: p.id, name: p.name || "", creative_url: p.creative_url || "", destination_url: p.destination_url || "" }); setShowForm(true); };

  const uploadCreative = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, creative_url: data.url }));
      toast.success("Creative uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Partner name is required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/premier-partners/${form.id}`, { name: form.name, creative_url: form.creative_url, destination_url: form.destination_url });
        toast.success("Partner updated");
      } else {
        await api.post("/admin/premier-partners", { name: form.name, creative_url: form.creative_url, destination_url: form.destination_url });
        toast.success("Partner added");
      }
      setShowForm(false); setForm(emptyForm); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save partner"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      if (p.is_active) { await api.post(`/admin/premier-partners/${p.id}/deactivate`); toast.success("Partner deactivated"); }
      else { await api.post(`/admin/premier-partners/${p.id}/activate`); toast.success(`${p.name} is now the active Premier Partner`); }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change status"); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-premier-partners">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy">Premier Partners</h1>
          <p className="text-sm text-muted-foreground">One active Premier Partner shows on every dealer dashboard.</p>
        </div>
        <Button onClick={openAdd} className="bg-navy hover:bg-navy-light text-white" data-testid="partner-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Partner
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="partner-form">
          <h2 className="font-serif text-xl font-bold text-navy">{form.id ? "Edit Partner" : "Add Partner"}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-navy">Partner name</label>
              <Input value={form.name} onChange={set("name")} placeholder="Elite Recreational Finance" data-testid="partner-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Destination URL (optional)</label>
              <Input value={form.destination_url} onChange={set("destination_url")} placeholder="https://partner.example.com" data-testid="partner-url-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy">Partner creative</label>
            <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-full sm:w-[320px] aspect-[8/3] rounded-xl border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0" data-testid="partner-creative-preview">
                {form.creative_url
                  ? <img src={form.creative_url} alt="Partner creative" className="w-full h-full object-contain" />
                  : <div className="text-slate-400 flex flex-col items-center gap-1"><ImageIcon className="w-6 h-6" /><span className="text-xs">No creative yet</span></div>}
              </div>
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCreative(e.target.files?.[0])} data-testid="partner-file-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="partner-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> {form.creative_url ? "Replace creative" : "Upload creative"}</>}
                </Button>
                <p className="text-xs text-muted-foreground max-w-[260px]">{RECOMMENDED} Keep logos and key copy about 50 to 60 px from the edges.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="partner-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save Partner"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowForm(false); setForm(emptyForm); }} data-testid="partner-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="partner-list">
        {loading ? (
          <div className="px-4 py-10 text-center text-muted-foreground">Loading...</div>
        ) : partners.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground">
            <Star className="w-9 h-9 mx-auto mb-2 opacity-40" />
            No partners yet. Add your first Premier Partner to feature it on the dashboard.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {partners.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`partner-row-${p.id}`}>
                <div className="w-28 aspect-[8/3] rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {p.creative_url
                    ? <img src={p.creative_url} alt="" className="w-full h-full object-contain" />
                    : <ImageIcon className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-navy truncate">{p.name}</span>
                    {p.is_active && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`partner-active-badge-${p.id}`}><CheckCircle2 className="w-3 h-3" /> Active</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.destination_url || "No destination URL"}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`partner-edit-${p.id}`}>Edit</Button>
                <Button onClick={() => toggleActive(p)} data-testid={`partner-toggle-${p.id}`}
                  className={p.is_active ? "bg-slate-200 text-navy hover:bg-slate-300" : "bg-gold-gradient gold-gradient text-navy font-bold"}>
                  {p.is_active ? "Set Inactive" : "Set Active"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
